/**
 * Gerenciador central de overlays/modais.
 *
 * Garante que apenas UM overlay receba foco/cliques por vez.
 * Quando um overlay abre, todos os outros recebem `requestClose()`,
 * exceto os que tiverem prioridade ESTRITAMENTE MAIOR (ex: onboarding gates
 * sempre bloqueiam o resto).
 *
 * Funciona em modo anônimo (não depende de auth) e é independente de Radix.
 */

import { releaseStuckOverlayLocks } from "./overlayPointerGuard";

export type OverlayId = string;

export interface OverlayRegistration {
  id: OverlayId;
  /** Maior = mais prioritário. Ex: 1000 = bloqueante de onboarding, 300 = FAB. */
  priority: number;
  /** Callback acionado quando o manager pede para esse overlay fechar. */
  requestClose: () => void;
}

type Entry = OverlayRegistration & { isOpen: boolean };

const registry = new Map<OverlayId, Entry>();
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => {
    try { l(); } catch { /* noop */ }
  });
}

export const overlayManager = {
  register(reg: OverlayRegistration) {
    registry.set(reg.id, { ...reg, isOpen: false });
  },
  unregister(id: OverlayId) {
    registry.delete(id);
    notify();
    // Um overlay pode ser desmontado ainda "aberto" (rota trocando, force-close).
    // Se o Radix não limpou o body, liberamos aqui.
    if (typeof window !== "undefined") {
      window.setTimeout(() => releaseStuckOverlayLocks(), 60);
    }
  },
  /**
   * Marca um overlay como aberto e fecha todos os outros com prioridade ≤.
   * Se houver overlay com prioridade ESTRITAMENTE MAIOR já aberto,
   * retorna `false` (o caller deve cancelar a abertura).
   */
  open(id: OverlayId): boolean {
    const entry = registry.get(id);
    if (!entry) return true; // se não registrado, deixa abrir (defensivo)

    // Verifica bloqueio por prioridade superior
    for (const other of registry.values()) {
      if (other.id !== id && other.isOpen && other.priority > entry.priority) {
        return false;
      }
    }

    // Fecha overlays com prioridade ≤
    for (const other of registry.values()) {
      if (other.id !== id && other.isOpen && other.priority <= entry.priority) {
        other.isOpen = false;
        try { other.requestClose(); } catch { /* noop */ }
      }
    }

    entry.isOpen = true;
    notify();
    return true;
  },
  close(id: OverlayId) {
    const entry = registry.get(id);
    if (!entry) return;
    if (entry.isOpen) {
      entry.isOpen = false;
      notify();
      if (typeof window !== "undefined") {
        window.setTimeout(() => releaseStuckOverlayLocks(), 60);
      }
    }
  },
  isBlocked(id: OverlayId): boolean {
    const entry = registry.get(id);
    if (!entry) return false;
    for (const other of registry.values()) {
      if (other.id !== id && other.isOpen && other.priority > entry.priority) {
        return true;
      }
    }
    return false;
  },
  hasOpenAbove(priority: number): boolean {
    for (const other of registry.values()) {
      if (other.isOpen && other.priority > priority) return true;
    }
    return false;
  },
  /**
   * Existe QUALQUER outro overlay aberto (inclusive de prioridade menor)?
   *
   * Usado por quem prefere ESPERAR a mãe fechar o que está fazendo em vez de
   * subir por cima — caso do pedido de push após o primeiro compromisso, que
   * não deve empilhar sobre o modal da agenda (DAILY_SHORTCUTS, 400).
   */
  hasAnyOpenExcept(id: OverlayId): boolean {
    for (const other of registry.values()) {
      if (other.id !== id && other.isOpen) return true;
    }
    return false;
  },

  /**
   * Fecha TODOS os overlays abertos (independente de prioridade).
   *
   * Usado quando a navegação tira a mãe da tela atual — caso do CTA de paywall,
   * que ia para /maternar/planos deixando o modal aberto por cima (BL-9).
   */
  closeAll() {
    for (const other of registry.values()) {
      if (other.isOpen) {
        other.isOpen = false;
        try { other.requestClose(); } catch { /* noop */ }
      }
    }
    notify();
    if (typeof window !== "undefined") {
      window.setTimeout(() => releaseStuckOverlayLocks(), 60);
    }
  },

  /**
   * Fecha o overlay ABERTO de maior prioridade e diz se fechou algo.
   * Usado pelo botão Voltar (A-3) como rede para overlays custom que não
   * escutam Escape.
   */
  closeTop(): boolean {
    let top: Entry | null = null;
    for (const other of registry.values()) {
      if (!other.isOpen) continue;
      if (!top || other.priority > top.priority) top = other;
    }
    if (!top) return false;
    top.isOpen = false;
    try { top.requestClose(); } catch { /* noop */ }
    notify();
    if (typeof window !== "undefined") {
      window.setTimeout(() => releaseStuckOverlayLocks(), 60);
    }
    return true;
  },

  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  /** Debug helper. */
  _snapshot() {
    return Array.from(registry.values()).map((e) => ({
      id: e.id, priority: e.priority, isOpen: e.isOpen,
    }));
  },
};

/** Prioridades canônicas. Centralize aqui para evitar conflitos. */
export const OVERLAY_PRIORITY = {
  /** Gate legal/consentimento (disclaimer médico + IA) — sempre no topo. */
  LEGAL_GATE: 1200,
  /**
   * Paywall (sempre resultado de clique explícito da usuária) — acima dos
   * overlays automáticos, abaixo do gate legal.
   */
  PAYWALL: 1150,

  /**
   * Chat aberto a partir do FirstSessionSpotlight. Precisa vencer o
   * EMOTIONAL_GATE (1000) e o próprio spotlight (1100) — que ainda está aberto
   * por um commit quando o CTA dispara — e nunca alcançar o PAYWALL (1150).
   * Relação travada em src/test/paywall/paywallOverlayPriority.test.tsx.
   */
  ASSISTANT_FAB_SPOTLIGHT: 1120,

  // Spotlight de boas-vindas é o PRIMEIRO toque da mãe — vem antes de tudo,
  // inclusive do EmotionalContextGate (que faz 3 perguntas em sequência).
  FIRST_SESSION_SPOTLIGHT: 1100,
  EMOTIONAL_GATE: 1000,
  /** Celebração da 1ª vitória (pós-conclusão). */
  FIRST_VICTORY: 700,
  /** Pedido de push no momento de vitória. */
  VICTORY_PUSH: 600,
  RITUAL: 500,
  NPS_PROMPT: 450,
  /** Drawer de notificações (ação secundária, nunca suprime paywall/gate legal). */
  NOTIFICATION_DRAWER: 420,
  DAILY_SHORTCUTS: 400,
  ASSISTANT_FAB: 300,
} as const;
